<template>
  <div class="demo-exec-dashboard" :class="{ 'is-loading': loading }">
    <div v-if="loading" class="demo-loading-bar" aria-hidden="true" />
    <div class="demo-filter-bar">
      <div class="demo-filter-fields">
        <div class="demo-filter-item">
          <label>Campus</label>
          <v-select
            v-model="filters.campuses"
            :options="campusOptions"
            :clearable="true"
            :multiple="true"
            :close-on-select="false"
            :loading="campusesLoading"
            label="branch"
            class="demo-select demo-select-multi"
            placeholder="All campuses"
          />
        </div>
        <div class="demo-filter-item">
          <label>Session</label>
          <v-select
            v-model="filters.session"
            :options="sessionOptions"
            :clearable="false"
            :loading="sessionsLoading"
            label="session"
            class="demo-select"
            placeholder="All"
          />
        </div>
        <div class="demo-filter-item">
          <label>Month</label>
          <v-select
            v-model="filters.month"
            :options="monthOptions"
            :clearable="false"
            :searchable="true"
            class="demo-select"
            placeholder="All months"
          />
        </div>
      </div>
      <button type="button" class="demo-excel-btn" @click="downloadExcel" v-if="false">
        <feather-icon icon="DownloadIcon" size="16" class="mr-50" />
        Download Excel
      </button>
    </div>

    <section class="demo-section">
      <div class="demo-section-head">
        <p class="demo-section-label">Student Management</p>
        <h4 class="demo-section-title">Enrollment Overview</h4>
      </div>
      <div class="demo-kpi-grid">
        <template v-if="loading">
          <div
            v-for="n in 5"
            :key="'skel-kpi-' + n"
            class="demo-kpi-card skel-kpi"
            :style="{ '--skel-delay': (n - 1) * 0.08 + 's' }"
          >
            <div class="skel-kpi-top">
              <span class="skel-bone skel-kpi-icon" />
              <span class="skel-bone skel-kpi-badge" />
            </div>
            <span class="skel-bone skel-kpi-value" />
            <span class="skel-bone skel-kpi-label" />
            <span class="skel-bone skel-kpi-track" />
          </div>
        </template>
        <template v-else>
          <div
            v-for="card in kpiCards"
            :key="card.key"
            class="demo-kpi-card is-clickable"
            :class="'accent-' + card.accent"
            role="button"
            tabindex="0"
            @click="openEnrollmentList(card)"
            @keydown.enter.prevent="openEnrollmentList(card)"
          >
            <div class="demo-kpi-top">
              <div class="demo-kpi-icon" :class="'icon-' + card.accent">
                <feather-icon :icon="card.icon" size="18" />
              </div>
              <span v-if="card.badge" class="demo-kpi-badge">
                <feather-icon icon="TrendingUpIcon" size="12" class="mr-25" />
                {{ card.badge }}
              </span>
            </div>
            <div class="demo-kpi-value">{{ formatNumber(card.value) }}</div>
            <div class="demo-kpi-label">{{ card.label }}</div>
            <div v-if="card.sub" class="demo-kpi-sub">{{ card.sub }}</div>
            <div v-if="card.percent != null" class="demo-kpi-track">
              <div
                class="demo-kpi-fill"
                :style="{ width: card.percent + '%' }"
              ></div>
            </div>
          </div>
        </template>
      </div>
    </section>

    <section class="demo-charts-row">
      <div
        class="demo-card demo-chart-card is-clickable"
        role="button"
        tabindex="0"
        @click="openFinanceModal('predictedRevenue')"
        @keydown.enter.prevent="openFinanceModal('predictedRevenue')"
      >
        <div class="demo-card-head">
          <span class="demo-card-icon orange">
            <feather-icon icon="DollarSignIcon" size="16" />
          </span>
          <h5>Predicted revenue</h5>
        </div>
        <div v-if="loading" class="skel-chart" aria-hidden="true">
          <div
            v-for="(h, i) in [48, 72, 58, 86, 64, 78, 52]"
            :key="'rev-bar-' + i"
            class="skel-chart-bar"
            :style="{ height: h + '%', '--skel-delay': i * 0.07 + 's' }"
          />
        </div>
        <div v-else @click.stop>
          <vue-apex-charts
            type="bar"
            height="280"
            :options="revenueChartOptions"
            :series="revenueSeries"
          />
        </div>
      </div>
      <div
        class="demo-card demo-chart-card is-clickable"
        role="button"
        tabindex="0"
        @click="openFinanceModal('recovery')"
        @keydown.enter.prevent="openFinanceModal('recovery')"
      >
        <div class="demo-card-head">
          <span class="demo-card-icon green">
            <feather-icon icon="RefreshCwIcon" size="16" />
          </span>
          <h5>Recovery</h5>
        </div>
        <div v-if="loading" class="skel-chart" aria-hidden="true">
          <div
            v-for="(h, i) in [62, 44, 80, 56, 70, 48, 76]"
            :key="'rec-bar-' + i"
            class="skel-chart-bar"
            :style="{ height: h + '%', '--skel-delay': i * 0.07 + 's' }"
          />
        </div>
        <div v-else @click.stop>
          <vue-apex-charts
            type="bar"
            height="280"
            :options="recoveryChartOptions"
            :series="recoverySeries"
          />
        </div>
      </div>
    </section>

    <section class="demo-card demo-table-card">
      <div class="demo-card-head">
        <span class="demo-card-icon purple">
          <feather-icon icon="ListIcon" size="16" />
        </span>
        <h5>Admissions — leads &amp; conversions</h5>
      </div>
      <div class="demo-table-wrap">
        <table class="demo-table">
          <thead>
            <tr>
              <th>Campus</th>
              <th>Leads</th>
              <th>Inquiries</th>
              <th>Conversions</th>
              <th>Current admis.</th>
              <th>New session</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="loading">
              <tr
                v-for="n in 5"
                :key="'skel-adm-' + n"
                class="skel-row"
                :style="{ '--skel-delay': (n - 1) * 0.06 + 's' }"
              >
                <td><span class="skel-bone skel-w-lg" /></td>
                <td><span class="skel-bone skel-w-sm" /></td>
                <td><span class="skel-bone skel-w-sm" /></td>
                <td><span class="skel-bone skel-pill" /></td>
                <td><span class="skel-bone skel-w-sm" /></td>
                <td><span class="skel-bone skel-w-sm" /></td>
              </tr>
            </template>
            <template v-else v-for="row in admissionsRows">
              <tr :key="'adm-' + (row.key || row.campus)"
                  :class="{'is-clickable group-row': row.isGroup}"
                  @click="row.isGroup ? toggleGroup('adm', row.groupKey) : null">
                <td class="campus-cell">
                  {{ row.campus }}
                  <feather-icon
                    v-if="row.isGroup"
                    :icon="isGroupOpen('adm', row.groupKey) ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </td>
                <td>
                  <button
                    type="button"
                    class="demo-metric-btn"
                    @click.stop="openAdmissionsColumn(row, 'leads', 'Leads')"
                  >
                    {{ formatNumber(row.leads) }}
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    class="demo-metric-btn"
                    @click.stop="openAdmissionsColumn(row, 'inquiries', 'Inquiries')"
                  >
                    {{ formatNumber(row.inquiries) }}
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    class="demo-metric-btn"
                    @click.stop="openAdmissionsColumn(row, 'conversions', 'Conversions')"
                  >
                    <span class="pill pill-blue">{{ row.conversions }}</span>
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    class="demo-metric-btn"
                    @click.stop="openAdmissionsColumn(row, 'currentAdmissions', 'Current admissions')"
                  >
                    {{ formatNumber(row.currentAdmissions) }}
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    class="demo-metric-btn"
                    @click.stop="openAdmissionsColumn(row, 'newSession', 'New session')"
                  >
                    {{ formatNumber(row.newSession) }}
                  </button>
                </td>
              </tr>
              <template v-if="row.isGroup && isGroupOpen('adm', row.groupKey)">
                <tr v-for="child in row.children" :key="'adm-child-' + (child.key || child.campus)" class="accordion-child-row">
                  <td class="campus-cell" style="padding-left: 2rem !important;"><span class="text-muted">↳</span> {{ child.campus }}</td>
                  <td>
                    <button type="button" class="demo-metric-btn" @click.stop="openAdmissionsColumn(child, 'leads', 'Leads')">
                      {{ formatNumber(child.leads) }}
                    </button>
                  </td>
                  <td>
                    <button type="button" class="demo-metric-btn" @click.stop="openAdmissionsColumn(child, 'inquiries', 'Inquiries')">
                      {{ formatNumber(child.inquiries) }}
                    </button>
                  </td>
                  <td>
                    <button type="button" class="demo-metric-btn" @click.stop="openAdmissionsColumn(child, 'conversions', 'Conversions')">
                      <span class="pill pill-blue">{{ child.conversions }}</span>
                    </button>
                  </td>
                  <td>
                    <button type="button" class="demo-metric-btn" @click.stop="openAdmissionsColumn(child, 'currentAdmissions', 'Current admissions')">
                      {{ formatNumber(child.currentAdmissions) }}
                    </button>
                  </td>
                  <td>
                    <button type="button" class="demo-metric-btn" @click.stop="openAdmissionsColumn(child, 'newSession', 'New session')">
                      {{ formatNumber(child.newSession) }}
                    </button>
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>
      </div>
    </section>

    <section class="demo-card demo-table-card">
      <div class="demo-card-head">
        <span class="demo-card-icon orange">
          <feather-icon icon="CornerUpLeftIcon" size="16" />
        </span>
        <h5>Student retention / withdrawal</h5>
      </div>
      <div class="demo-table-wrap">
        <table class="demo-table">
          <thead>
            <tr>
              <th>Campus</th>
              <th>Total</th>
              <th>Initiated</th>
              <th>Retention</th>
              <th>Confirmed</th>
              <th>Reasons</th>
              <th class="text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="loading">
              <tr
                v-for="n in 5"
                :key="'skel-st-' + n"
                class="skel-row"
                :style="{ '--skel-delay': (n - 1) * 0.06 + 's' }"
              >
                <td><span class="skel-bone skel-w-lg" /></td>
                <td><span class="skel-bone skel-w-sm" /></td>
                <td><span class="skel-bone skel-w-sm" /></td>
                <td><span class="skel-bone skel-pill" /></td>
                <td><span class="skel-bone skel-w-sm" /></td>
                <td><span class="skel-bone skel-btn" /></td>
                <td class="text-right"><span class="skel-bone skel-btn" /></td>
              </tr>
            </template>
            <template v-else v-for="row in studentRetentionRows">
              <tr :key="'st-' + (row.key || row.campus)"
                  :class="{'is-clickable group-row': row.isGroup}"
                  @click="row.isGroup ? toggleGroup('st', row.groupKey) : null">
                <td class="campus-cell">
                  {{ row.campus }}
                  <feather-icon
                    v-if="row.isGroup"
                    :icon="isGroupOpen('st', row.groupKey) ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </td>
                <td>
                  <button type="button" class="demo-metric-btn" @click.stop="openStudentRetentionColumn(row, 'total', 'Total students')">
                    {{ formatNumber(row.total) }}
                  </button>
                </td>
                <td>
                  <div class="d-flex flex-column align-items-center justify-content-center">
                    <button type="button" class="demo-metric-btn" @click.stop="openStudentRetentionColumn(row, 'initiated', 'Initiated')">
                      {{ formatNumber(row.initiated) }}
                    </button>
                    <div v-if="row.parentInitiated > 0 || row.admissionOfficerCreated > 0" class="d-flex align-items-center mt-25 text-nowrap" style="gap: 6px; font-size: 0.75rem; color: #64748b; font-weight: 600;">
                      <span v-b-tooltip.hover.top="'Parent Initiated'" title="Parent Initiated" class="d-flex align-items-center">
                        <feather-icon icon="UserIcon" size="10" class="mr-25" />
                        {{ formatNumber(row.parentInitiated || 0) }}
                      </span>
                      <span style="color: #cbd5e1;">|</span>
                      <span v-b-tooltip.hover.top="'Admission Officer Created'" title="Admission Officer Created" class="d-flex align-items-center">
                        <feather-icon icon="BriefcaseIcon" size="10" class="mr-25" />
                        {{ formatNumber(row.admissionOfficerCreated || 0) }}
                      </span>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="pill" :class="retentionPillClass(row.retention)">
                    {{ row.retention }}%
                  </span>
                </td>
                <td>
                  <button type="button" class="demo-metric-btn" @click.stop="openStudentRetentionColumn(row, 'left', 'Left / confirmed')">
                    {{ formatNumber(row.confirmed) }}
                  </button>
                </td>
                <td>
                  <button type="button" class="demo-action-btn" @click.stop="openReasons('Student', row)">
                    <feather-icon icon="InfoIcon" size="14" class="mr-25" />
                    Reasons
                  </button>
                </td>
                <td class="text-right">
                  <button type="button" class="demo-action-btn" @click.stop="openStudentRetentionColumn(row, 'retained', 'Retained')">
                    <feather-icon icon="EyeIcon" size="14" class="mr-25" />
                    View details
                  </button>
                </td>
              </tr>
              <template v-if="row.isGroup && isGroupOpen('st', row.groupKey)">
                <tr v-for="child in row.children" :key="'st-child-' + (child.key || child.campus)" class="accordion-child-row">
                  <td class="campus-cell" style="padding-left: 2rem !important;"><span class="text-muted">↳</span> {{ child.campus }}</td>
                  <td>
                    <button type="button" class="demo-metric-btn" @click.stop="openStudentRetentionColumn(child, 'total', 'Total students')">
                      {{ formatNumber(child.total) }}
                    </button>
                  </td>
                  <td>
                    <div class="d-flex flex-column align-items-center justify-content-center">
                      <button type="button" class="demo-metric-btn" @click.stop="openStudentRetentionColumn(child, 'initiated', 'Initiated')">
                        {{ formatNumber(child.initiated) }}
                      </button>
                      <div v-if="child.parentInitiated > 0 || child.admissionOfficerCreated > 0" class="d-flex align-items-center mt-25 text-nowrap" style="gap: 6px; font-size: 0.75rem; color: #64748b; font-weight: 600;">
                        <span v-b-tooltip.hover.top="'Parent Initiated'" title="Parent Initiated" class="d-flex align-items-center">
                          <feather-icon icon="UserIcon" size="10" class="mr-25" />
                          {{ formatNumber(child.parentInitiated || 0) }}
                        </span>
                        <span style="color: #cbd5e1;">|</span>
                        <span v-b-tooltip.hover.top="'Admission Officer Created'" title="Admission Officer Created" class="d-flex align-items-center">
                          <feather-icon icon="BriefcaseIcon" size="10" class="mr-25" />
                          {{ formatNumber(child.admissionOfficerCreated || 0) }}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class="pill" :class="retentionPillClass(child.retention)">
                      {{ child.retention }}%
                    </span>
                  </td>
                  <td>
                    <button type="button" class="demo-metric-btn" @click.stop="openStudentRetentionColumn(child, 'left', 'Left / confirmed')">
                      {{ formatNumber(child.confirmed) }}
                    </button>
                  </td>
                  <td>
                    <button type="button" class="demo-action-btn" @click.stop="openReasons('Student', child)">
                      <feather-icon icon="InfoIcon" size="14" class="mr-25" />
                      Reasons
                    </button>
                  </td>
                  <td class="text-right">
                    <button type="button" class="demo-action-btn" @click.stop="openStudentRetentionColumn(child, 'retained', 'Retained')">
                      <feather-icon icon="EyeIcon" size="14" class="mr-25" />
                      View details
                    </button>
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>
      </div>
    </section>

    <section class="demo-card demo-table-card">
      <div class="demo-card-head">
        <span class="demo-card-icon green">
          <feather-icon icon="MonitorIcon" size="16" />
        </span>
        <h5>Teacher retention / withdrawal</h5>
      </div>
      <div class="demo-table-wrap">
        <table class="demo-table">
          <thead>
            <tr>
              <th>Campus</th>
              <th>Initiated</th>
              <th>Retention</th>
              <th>Confirmed</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="loading">
              <tr
                v-for="n in 5"
                :key="'skel-te-' + n"
                class="skel-row"
                :style="{ '--skel-delay': (n - 1) * 0.06 + 's' }"
              >
                <td><span class="skel-bone skel-w-lg" /></td>
                <td><span class="skel-bone skel-w-sm" /></td>
                <td><span class="skel-bone skel-pill" /></td>
                <td><span class="skel-bone skel-w-sm" /></td>
                <td class="text-right">
                  <span class="skel-bone skel-btn" />
                  <span class="skel-bone skel-btn" />
                </td>
              </tr>
            </template>
            <template v-else v-for="row in teacherRetentionRows">
              <tr :key="'te-' + (row.key || row.campus)"
                  :class="{'is-clickable group-row': row.isGroup}"
                  @click="row.isGroup ? toggleGroup('te', row.groupKey) : null">
                <td class="campus-cell">
                  {{ row.campus }}
                  <feather-icon
                    v-if="row.isGroup"
                    :icon="isGroupOpen('te', row.groupKey) ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </td>
                <td>{{ formatNumber(row.initiated) }}</td>
                <td>
                  <span class="pill" :class="retentionPillClass(row.retention)">
                    {{ row.retention }}%
                  </span>
                </td>
                <td>
                  <button type="button" class="demo-metric-btn" @click.stop="openTeacherRetentionColumn(row, 'left', 'Left staff')">
                    {{ formatNumber(row.confirmed) }}
                  </button>
                </td>
                <td class="text-right actions-cell">
                  <button type="button" class="demo-action-btn" @click.stop="openReasons('Teacher', row)">
                    <feather-icon icon="InfoIcon" size="14" class="mr-25" />
                    Reasons
                  </button>
                  <button type="button" class="demo-action-btn" @click.stop="openTeacherRetentionColumn(row, 'left', 'Left staff')">
                    <feather-icon icon="EyeIcon" size="14" class="mr-25" />
                    View details
                  </button>
                </td>
              </tr>
              <template v-if="row.isGroup && isGroupOpen('te', row.groupKey)">
                <tr v-for="child in row.children" :key="'te-child-' + (child.key || child.campus)" class="accordion-child-row">
                  <td class="campus-cell" style="padding-left: 2rem !important;"><span class="text-muted">↳</span> {{ child.campus }}</td>
                  <td>{{ formatNumber(child.initiated) }}</td>
                  <td>
                    <span class="pill" :class="retentionPillClass(child.retention)">
                      {{ child.retention }}%
                    </span>
                  </td>
                  <td>
                    <button type="button" class="demo-metric-btn" @click.stop="openTeacherRetentionColumn(child, 'left', 'Left staff')">
                      {{ formatNumber(child.confirmed) }}
                    </button>
                  </td>
                  <td class="text-right actions-cell">
                    <button type="button" class="demo-action-btn" @click.stop="openReasons('Teacher', child)">
                      <feather-icon icon="InfoIcon" size="14" class="mr-25" />
                      Reasons
                    </button>
                    <button type="button" class="demo-action-btn" @click.stop="openTeacherRetentionColumn(child, 'left', 'Left staff')">
                      <feather-icon icon="EyeIcon" size="14" class="mr-25" />
                      View details
                    </button>
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>
      </div>
    </section>

    <section class="demo-charts-row" style="margin-bottom: 2rem;" v-if="false">
      <div class="demo-card demo-chart-card" style="width: 100%;">
        <div class="demo-card-head">
          <span class="demo-card-icon" style="background-color: #ffe4e6; color: #e11d48;">
            <feather-icon icon="BarChart2Icon" size="16" />
          </span>
          <h5>Exam Grade Distribution</h5>
        </div>
        <div v-if="loading" class="skel-chart" aria-hidden="true">
          <div
            v-for="(h, i) in [70, 50, 90, 60, 40, 30]"
            :key="'exam-bar-' + i"
            class="skel-chart-bar"
            :style="{ height: h + '%', '--skel-delay': i * 0.07 + 's' }"
          />
        </div>
        <div v-else>
          <vue-apex-charts
            type="bar"
            height="280"
            :options="examGradeChartOptions"
            :series="examGradeSeries"
          />
        </div>
      </div>
    </section>

    <b-modal
      id="demo-finance-modal"
      v-model="financeModal.show"
      centered
      size="xl"
      hide-header
      hide-footer
      modal-class="demo-list-modal"
      content-class="demo-list-modal-content"
      body-class="demo-list-modal-body"
    >
      <div class="demo-list-shell">
        <div class="demo-list-header">
          <div class="demo-list-header-text">
            <div class="demo-list-title">{{ financeModal.title }}</div>
            <p class="demo-list-sub">
              {{ formatNumber(financeCampusBlocks.length) }} campuses
              <span>·</span>
              {{ selectedCampusLabel }}
              <span>·</span>
              {{ selectedSessionLabel }}
              <span>·</span>
              {{ financeModal.focusMonth || filters.month }}
            </p>
          </div>
          <button
            type="button"
            class="demo-list-close"
            aria-label="Close"
            @click="financeModal.show = false"
          >
            <feather-icon icon="XIcon" size="18" />
          </button>
        </div>

        <div
          v-if="!financeCampusBlocks.length"
          class="demo-reason-empty"
        >
          No campus fee type records found.
        </div>

        <div v-else class="demo-finance-blocks">
          <div
            v-for="(block, bIdx) in financeCampusBlocks"
            :key="'fin-block-' + bIdx"
            class="demo-campus-card"
          >
            <div class="demo-campus-card-head">
              <div class="demo-campus-card-info">
                <div class="demo-campus-card-name">{{ block.campusName }}</div>
                <div class="demo-campus-card-meta">
                  {{ block.type }} · {{ block.feeTypes.length }} fee
                  type{{ block.feeTypes.length === 1 ? "" : "s" }}
                </div>
              </div>
              <div class="demo-campus-card-total">
                <span>{{ financeModal.metricLabel }}</span>
                <strong>{{ formatMoney(block.amount) }}</strong>
              </div>
            </div>
            <div class="demo-campus-card-body">
              <div
                v-for="(ft, ftIdx) in block.feeTypes"
                :key="'ft-' + bIdx + '-' + ftIdx"
                class="demo-campus-fee-item"
              >
                <span>{{ ft.feeType }}</span>
                <strong>{{ formatMoney(ft.amount) }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="demo-list-modal"
      v-model="listModal.show"
      centered
      size="xl"
      hide-header
      hide-footer
      no-close-on-backdrop
      modal-class="demo-list-modal"
      content-class="demo-list-modal-content"
      body-class="demo-list-modal-body"
    >
      <div class="demo-list-shell">
        <div class="demo-list-header">
          <div class="demo-list-header-text">
            <h5 class="demo-list-title">{{ listModal.title }}</h5>
            <p class="demo-list-sub">
              {{ formatNumber(listModal.count) }} records
              <span>·</span>
              {{ selectedCampusLabel }}
              <span>·</span>
              {{ selectedSessionLabel }}
              <span>·</span>
              {{ filters.month }}
            </p>
          </div>
          <button
            type="button"
            class="demo-list-close"
            aria-label="Close"
            @click="listModal.show = false"
          >
            <feather-icon icon="XIcon" size="18" />
          </button>
        </div>

        <div v-if="listModal.loading" class="demo-list-loading">
          <div
            v-for="n in 6"
            :key="'list-skel-' + n"
            class="demo-list-skel-row"
          >
            <span class="skel-bone skel-w-lg" />
            <span class="skel-bone skel-w-sm" />
            <span class="skel-bone skel-w-sm" />
            <span class="skel-bone skel-btn" />
          </div>
        </div>

        <div v-else-if="!listModal.list.length" class="demo-reason-empty">
          No records found for this filter.
        </div>

        <div v-else class="demo-list-table-wrap">
          <table class="demo-list-table">
            <thead>
              <tr>
                <th class="col-index">#</th>
                <th
                  v-for="col in listModal.columns"
                  :key="col"
                  :class="{
                    'col-campus': isCampusColumn(col),
                    'col-name': isNameColumn(col),
                  }"
                >
                  {{ formatListHeader(col) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in listModal.list"
                :key="'enr-' + idx"
              >
                <td class="col-index">{{ idx + 1 }}</td>
                <td
                  v-for="col in listModal.columns"
                  :key="col"
                  :class="{
                    'col-campus': isCampusColumn(col),
                    'col-name': isNameColumn(col),
                    'col-status': isStatusColumn(col),
                  }"
                >
                  <span v-if="isStatusColumn(col)" class="pill" :class="statusChipClass(row[col])">
                    {{ formatListCell(row[col]) }}
                  </span>
                  <template v-else-if="col === 'initiatedBy' || col === 'initiated_by'">
                    {{ (row.parentInitiated || row.ParentInitiated || row.isParentInitiated || row.isParent || row.parent_initiated) ? 'Parent' : ((row.admissionOfficerCreated || row.AdmissionOfficerCreated || row.isAdmissionOfficerCreated || row.isAdmissionOfficer || row.admission_officer_created || row.officerInitiated || row.isOfficer) ? 'Admission Officer' : formatListCell(row[col])) }}
                  </template>
                  <template v-else>
                    {{ formatListCell(row[col]) }}
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="demo-details-modal"
      v-model="detailsModal.show"
      centered
      hide-footer
      modal-class="demo-dark-modal"
      content-class="demo-dark-modal-content"
      header-class="demo-dark-modal-header"
      body-class="demo-dark-modal-body"
      title-class="demo-dark-modal-title"
      :title="detailsModal.title"
    >
      <div v-if="detailsModal.row" class="demo-modal-grid">
        <div
          v-for="(value, key) in detailsModal.row"
          :key="key"
          class="demo-modal-item"
        >
          <span class="demo-modal-key">{{ formatKey(key) }}</span>
          <span class="demo-modal-val">{{ formatListCell(value) }}</span>
        </div>
      </div>
      <div
        v-if="detailsModal.reasons && detailsModal.reasons.length"
        class="demo-modal-reasons"
      >
        <span class="demo-modal-key">Reasons</span>
        <div class="demo-reason-list">
          <div
            v-for="reason in detailsModal.reasons"
            :key="reason.label"
            class="demo-reason-row"
          >
            <span class="demo-reason-label">{{ reason.label }}</span>
            <span class="pill pill-blue">{{ reason.count }}</span>
          </div>
        </div>
      </div>
      <p class="demo-modal-note">
        {{ selectedCampusLabel }} · {{ selectedSessionLabel }} ·
        {{ filters.month }}
      </p>
    </b-modal>

    <b-modal
      id="demo-reasons-modal"
      v-model="reasonsModal.show"
      centered
      size="lg"
      hide-header
      hide-footer
      modal-class="demo-list-modal"
      content-class="demo-list-modal-content"
      body-class="demo-list-modal-body"
    >
      <div class="demo-list-shell">
        <div class="demo-list-header">
          <div class="demo-list-header-text">
            <div class="demo-list-title">{{ reasonsModal.title }}</div>
            <p class="demo-list-sub">
              {{ formatNumber(reasonsModal.items.length) }} reasons
              <span>·</span>
              {{ selectedCampusLabel }}
              <span>·</span>
              {{ selectedSessionLabel }}
              <span>·</span>
              {{ filters.month }}
            </p>
          </div>
          
          <button
            type="button"
            class="demo-list-close"
            aria-label="Close"
            @click="reasonsModal.show = false"
          >
            <feather-icon icon="XIcon" size="18" />
          </button>
        </div>

        <div v-if="reasonsModal.loading" class="demo-list-loading">
          <div
            v-for="n in 4"
            :key="'reason-skel-' + n"
            class="demo-list-skel-row"
          >
            <span class="skel-bone skel-w-lg" />
            <span class="skel-bone skel-w-sm" />
          </div>
        </div>

        <div
          v-else-if="!reasonsModal.items.length"
          class="demo-reason-empty"
        >
          No withdrawal reasons for this campus.
        </div>

        <div v-else class="demo-reason-list demo-reason-list-scroll">
          <div
            v-for="(reason, idx) in reasonsModal.items"
            :key="reason.label + '-' + idx"
            class="demo-reason-block"
          >
            <button
              type="button"
              class="demo-reason-row is-expandable"
              @click="toggleReasonStudents(idx)"
            >
              <span class="demo-reason-label">
                <feather-icon
                  v-if="reason.people && reason.people.length"
                  :icon="reason.open ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                  size="14"
                  class="mr-25"
                />
                {{ reason.label }}
              </span>
              <span class="demo-reason-count">{{ reason.count }}</span>
            </button>
            <div
              v-if="reason.open && reason.people && reason.people.length"
              class="demo-reason-students"
            >
              <div class="demo-list-table-wrap demo-reason-people-wrap">
                <table class="demo-list-table">
                  <thead>
                    <tr>
                      <th class="col-index">#</th>
                      <th
                        v-for="col in reason.columns"
                        :key="col"
                        :class="{
                          'col-campus': isCampusColumn(col),
                          'col-name': isNameColumn(col),
                        }"
                      >
                        {{ formatListHeader(col) }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(person, sIdx) in reason.people"
                      :key="'rs-' + idx + '-' + sIdx"
                    >
                      <td class="col-index">{{ sIdx + 1 }}</td>
                      <td
                        v-for="col in reason.columns"
                        :key="col"
                        :class="{
                          'col-campus': isCampusColumn(col),
                          'col-name': isNameColumn(col),
                        }"
                      >
                        <span v-if="isStatusColumn(col)" class="pill" :class="statusChipClass(person[col])">
                          {{ formatListCell(person[col]) }}
                        </span>
                        <template v-else-if="col === 'initiatedBy' || col === 'initiated_by'">
                          {{ (person.parentInitiated || person.ParentInitiated || person.isParentInitiated || person.isParent || person.parent_initiated) ? 'Parent' : ((person.admissionOfficerCreated || person.AdmissionOfficerCreated || person.isAdmissionOfficerCreated || person.isAdmissionOfficer || person.admission_officer_created || person.officerInitiated || person.isOfficer) ? 'Admission Officer' : formatListCell(person[col])) }}
                        </template>
                        <template v-else>
                          {{ formatListCell(person[col]) }}
                        </template>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { BModal } from "bootstrap-vue";
import vSelect from "vue-select";
import VueApexCharts from "vue-apexcharts";
import { mapActions } from "vuex";

const MONTH_LABELS = ["Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
const FILTER_MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const MONTH_NUM = {
  "All months": 0,
  Jan: 1,
  Feb: 2,
  Mar: 3,
  Apr: 4,
  May: 5,
  Jun: 6,
  Jul: 7,
  Aug: 8,
  Sep: 9,
  Oct: 10,
  Nov: 11,
  Dec: 12,
};
const CURRENT_SESSION = { id: 0, session: "All" };

function formatMoneyTick(val) {
  const n = Number(val) || 0;
  if (Math.abs(n) >= 1000000) return `Rs ${(n / 1000000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return `Rs ${Math.round(n / 1000)}k`;
  return `Rs ${Math.round(n)}`;
}

export default {
  name: "DemoExecutiveDashboard",
  components: {
    BModal,
    vSelect,
    VueApexCharts,
  },
  data() {
    return {
      ready: false,
      loading: false,
      filters: {
        campuses: [],
        session: CURRENT_SESSION,
        month: "All months",
      },
      campusesLoading: false,
      sessionsLoading: false,
      campusOptions: [],
      sessionOptions: [CURRENT_SESSION],
      monthOptions: ["All months", ...FILTER_MONTH_LABELS],
      enrollmentOverview: null,
      admissionsRowsData: [],
      studentRetentionRowsData: [],
      teacherRetentionRowsData: [],
      chartLabels: [...MONTH_LABELS],
      predictedRevenueData: [],
      recoveryData: [],
      revenueRecoveryDetails: null,
      detailsModal: {
        show: false,
        title: "",
        row: null,
        reasons: [],
      },
      listModal: {
        show: false,
        title: "",
        loading: false,
        key: "",
        count: 0,
        list: [],
        columns: [],
      },
      financeModal: {
        show: false,
        title: "",
        metric: "predictedRevenue",
        metricLabel: "Predicted revenue",
        focusMonth: null,
      },
      reasonsModal: {
        show: false,
        title: "",
        loading: false,
        items: [],
      },
      openGroups: {},
      examGradeData: [],
    };
  },
  computed: {
    selectedCampusLabel() {
      const selected = Array.isArray(this.filters.campuses)
        ? this.filters.campuses
        : [];
      if (!selected.length) return "All campuses";
      return selected
        .map((c) => (c && (c.branch || c.name)) || "Campus")
        .join(", ");
    },
    selectedSessionLabel() {
      if (!this.filters.session) return "Current session";
      if (typeof this.filters.session === "string") return this.filters.session;
      return this.filters.session.session || "Current session";
    },
    apiSessionName() {
      const sel = this.filters.session;
      if (!sel || sel.id === 0 || sel.session === "Current session") {
        const current = this.sessionOptions.find(
          (s) => s && s.isCurrentSession
        );
        return (current && current.session) || "";
      }
      return sel.session || "";
    },
    /** Comma-separated campus IDs, or "0" when none selected (all campuses) */
    apiCampusId() {
      const selected = Array.isArray(this.filters.campuses)
        ? this.filters.campuses
        : [];
      const ids = selected
        .map((c) => (c && c.id != null ? c.id : null))
        .filter((id) => {
          if (id == null) return false;
          if (id === "coc" || id === "np") return true;
          const num = Number(id);
          return !Number.isNaN(num) && num > 0;
        });
      if (!ids.length) return "0";
      return ids.join(",");
    },
    apiMonth() {
      const m = this.filters.month;
      if (m == null || m === "All months") return 0;
      if (typeof m === "number") return m;
      return MONTH_NUM[m] != null ? MONTH_NUM[m] : 0;
    },
    isAllCampuses() {
      return (
        !Array.isArray(this.filters.campuses) ||
        this.filters.campuses.length === 0
      );
    },
    kpiCards() {
      const d = this.enrollmentOverview;
      if (!d) {
        return [
          {
            key: "overallEnrolled",
            icon: "UsersIcon",
            accent: "blue",
            value: 0,
            label: "Overall Enrolled",
            badge: this.loading ? "Loading" : "—",
          },
          {
            key: "studentsCompany",
            icon: "UserIcon",
            accent: "purple",
            value: 0,
            label: "Students (company)",
            sub: "—",
            percent: 0,
          },
          {
            key: "studentsNetwork",
            icon: "Share2Icon",
            accent: "teal",
            value: 0,
            label: "Students (network)",
            sub: "—",
            percent: 0,
          },
          {
            key: "totalStaff",
            icon: "UserCheckIcon",
            accent: "green",
            value: 0,
            label: "Total Staff",
            sub: "Across campuses",
          },
          {
            key: "staffCompany",
            icon: "BriefcaseIcon",
            accent: "orange",
            value: 0,
            label: "Staff (company)",
            sub: "—",
            percent: 0,
          },
          {
            key: "staffNetwork",
            icon: "GlobeIcon",
            accent: "pink",
            value: 0,
            label: "Staff (network)",
            sub: "—",
            percent: 0,
          },
        ];
      }

      const stCompanyPct = Math.round(Number(d.studentsCompanyPercent) || 0);
      const stNetworkPct = Math.round(Number(d.studentsNetworkPercent) || 0);
      const staffCompanyPct = Math.round(Number(d.staffCompanyPercent) || 0);
      const staffNetworkPct = Math.round(Number(d.staffNetworkPercent) || 0);

      return [
        {
          key: "overallEnrolled",
          icon: "UsersIcon",
          accent: "blue",
          value: d.overallEnrolled || 0,
          label: "Overall Enrolled",
          badge: "Active",
        },
        {
          key: "studentsCompany",
          icon: "UserIcon",
          accent: "purple",
          value: d.studentsCompany || 0,
          label: "Students (company)",
          sub: `${stCompanyPct}% of total`,
          percent: stCompanyPct,
        },
        {
          key: "studentsNetwork",
          icon: "Share2Icon",
          accent: "teal",
          value: d.studentsNetwork || 0,
          label: "Students (network)",
          sub: `${stNetworkPct}% of total`,
          percent: stNetworkPct,
        },
        {
          key: "totalStaff",
          icon: "UserCheckIcon",
          accent: "green",
          value: d.totalStaff || 0,
          label: "Total Staff",
          sub: "Across campuses",
        },
        {
          key: "staffCompany",
          icon: "BriefcaseIcon",
          accent: "orange",
          value: d.staffCompany || 0,
          label: "Staff (company)",
          sub: `${staffCompanyPct}% of staff`,
          percent: staffCompanyPct,
        },
        {
          key: "staffNetwork",
          icon: "GlobeIcon",
          accent: "pink",
          value: d.staffNetwork || 0,
          label: "Staff (network)",
          sub: `${staffNetworkPct}% of staff`,
          percent: staffNetworkPct,
        },
      ];
    },
    revenueSeries() {
      return [
        {
          name: "Predicted revenue",
          data:
            this.predictedRevenueData.length > 0
              ? this.predictedRevenueData
              : MONTH_LABELS.map(() => 0),
        },
      ];
    },
    recoverySeries() {
      return [
        {
          name: "Recovery",
          data:
            this.recoveryData.length > 0
              ? this.recoveryData
              : MONTH_LABELS.map(() => 0),
        },
      ];
    },
    financeCampusBlocks() {
      const details = this.revenueRecoveryDetails || {};
      const isRecovery = this.financeModal.metric === "recovery";
      const focus = this.financeModal.focusMonth;
      const amountOf = (obj = {}) => {
        const monthRow = this.findMonthRow(obj.months, focus);
        if (monthRow) {
          return Number(
            isRecovery
              ? monthRow.recovery != null
                ? monthRow.recovery
                : 0
              : monthRow.predictedRevenue != null
              ? monthRow.predictedRevenue
              : 0
          );
        }
        return Number(
          isRecovery
            ? obj.recovery != null
              ? obj.recovery
              : obj.Recovery != null
              ? obj.Recovery
              : 0
            : obj.predictedRevenue != null
            ? obj.predictedRevenue
            : obj.PredictedRevenue != null
            ? obj.PredictedRevenue
            : 0
        );
      };
      const feeTypeName = (ft = {}) =>
        ft.feeType ||
        ft.FeeType ||
        ft.name ||
        ft.Name ||
        ft.fee_type ||
        "Fee type";
      const campusNameOf = (c = {}) =>
        c.campusName ||
        c.CampusName ||
        c.campus ||
        c.Campus ||
        c.name ||
        c.Name ||
        c.branch ||
        c.Branch ||
        "Campus";
      const feeTypesOf = (c = {}) => {
        if (Array.isArray(c.feeTypes)) return c.feeTypes;
        if (Array.isArray(c.FeeTypes)) return c.FeeTypes;
        if (Array.isArray(c.feeTypeWise)) return c.feeTypeWise;
        if (Array.isArray(c.FeeTypeWise)) return c.FeeTypeWise;
        return [];
      };

      let campuses = [];
      if (Array.isArray(details.campusWise)) campuses = details.campusWise;
      else if (Array.isArray(details.CampusWise)) campuses = details.CampusWise;

      if (!campuses.length) {
        const overallFeeTypes = Array.isArray(details.feeTypeWise)
          ? details.feeTypeWise
          : Array.isArray(details.FeeTypeWise)
          ? details.FeeTypeWise
          : [];
        if (!overallFeeTypes.length) return [];
        const feeTypes = overallFeeTypes.map((ft) => ({
          feeType: feeTypeName(ft),
          amount: amountOf(ft),
        }));
        return [
          {
            campusID: "all",
            campusName: "All campuses",
            type: "—",
            feeTypes,
            amount: feeTypes.reduce((s, ft) => s + (Number(ft.amount) || 0), 0),
          },
        ];
      }

      return campuses.map((campus, idx) => {
        const sourceFeeTypes = feeTypesOf(campus);
        const feeTypes = (
          sourceFeeTypes.length
            ? sourceFeeTypes
            : [
                {
                  feeType: "Overall",
                  predictedRevenue: campus.predictedRevenue,
                  recovery: campus.recovery,
                  months: campus.months,
                },
              ]
        ).map((ft) => ({
          feeType: feeTypeName(ft),
          amount: amountOf({ ...ft, months: ft.months || campus.months }),
        }));

        return {
          campusID: campus.campusID || campus.CampusID || idx,
          campusName: campusNameOf(campus),
          type: campus.type || campus.Type || campus.campusType || "—",
          feeTypes,
          amount: feeTypes.reduce((s, ft) => s + (Number(ft.amount) || 0), 0),
        };
      });
    },
    chartBase() {
      return {
        chart: {
          toolbar: { show: false },
          background: "transparent",
          foreColor: "#8a86b7",
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 700,
          },
        },
        theme: { mode: "light" },
        grid: {
          borderColor: "#f0edff",
          strokeDashArray: 4,
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories:
            this.chartLabels.length > 0 ? this.chartLabels : MONTH_LABELS,
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { style: { colors: "#8a86b7" } },
        },
        yaxis: {
          labels: {
            style: { colors: "#8a86b7" },
            formatter: (val) => formatMoneyTick(val),
          },
        },
        tooltip: {
          theme: "light",
          y: {
            formatter: (val) =>
              `Rs ${Number(val || 0).toLocaleString("en-PK")}`,
          },
        },
        plotOptions: {
          bar: {
            borderRadius: 6,
            columnWidth: "48%",
          },
        },
      };
    },
    revenueChartOptions() {
      return {
        ...this.chartBase,
        chart: {
          ...this.chartBase.chart,
          events: {
            click: (_e, _ctx, cfg) => {
              if (cfg.dataPointIndex == null || cfg.dataPointIndex < 0) return;
              const label =
                this.chartLabels[cfg.dataPointIndex] != null
                  ? this.chartLabels[cfg.dataPointIndex]
                  : null;
              this.openFinanceModal("predictedRevenue", label);
            },
          },
        },
        colors: ["#7c5dfa"],
        fill: {
          type: "gradient",
          gradient: {
            shade: "light",
            type: "vertical",
            gradientToColors: ["#cbb9ff"],
            opacityFrom: 1,
            opacityTo: 0.85,
          },
        },
      };
    },
    recoveryChartOptions() {
      return {
        ...this.chartBase,
        chart: {
          ...this.chartBase.chart,
          events: {
            click: (_e, _ctx, cfg) => {
              if (cfg.dataPointIndex == null || cfg.dataPointIndex < 0) return;
              const label =
                this.chartLabels[cfg.dataPointIndex] != null
                  ? this.chartLabels[cfg.dataPointIndex]
                  : null;
              this.openFinanceModal("recovery", label);
            },
          },
        },
        colors: ["#2dd4bf"],
        fill: {
          type: "gradient",
          gradient: {
            shade: "light",
            type: "vertical",
            gradientToColors: ["#99f6e4"],
            opacityFrom: 1,
            opacityTo: 0.8,
          },
        },
      };
    },
    admissionsRows() {
      return this.admissionsRowsData;
    },
    studentRetentionRows() {
      return this.studentRetentionRowsData;
    },
    teacherRetentionRows() {
      return this.teacherRetentionRowsData;
    },
    examGradeSeries() {
      return [
        {
          name: "Students",
          data: this.examGradeData.map((item) => item.count || 0),
        },
      ];
    },
    examGradeChartOptions() {
      const labels = this.examGradeData.map((item) => item.grade || "N/A");
      return {
        ...this.chartBase,
        xaxis: {
          ...this.chartBase.xaxis,
          categories: labels,
        },
        colors: ["#f43f5e"], // a pink/red theme to distinguish from others
        fill: {
          type: "gradient",
          gradient: {
            shade: "light",
            type: "vertical",
            gradientToColors: ["#fda4af"],
            opacityFrom: 1,
            opacityTo: 0.8,
          },
        },
        tooltip: {
          theme: "dark",
        },
      };
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        if (this.ready) this.loadDashboardData();
      },
    },
  },
  async created() {
    await Promise.all([this.loadCampuses(), this.loadSessions()]);
    this.ready = true;
    await this.loadDashboardData();
  },
  methods: {
    ...mapActions({ get: "get" }),
    parseNestedDashboardData(data, mapFn) {
      if (!data) return [];
      if (Array.isArray(data)) {
        return this.buildAccordionRows(data.map(mapFn));
      }
      
      const result = [];
      
      if (data.overall) {
        result.push({ ...mapFn(data.overall), isGroup: false });
      }
      
      if (data.coc && data.coc.overall) {
        result.push({
          ...mapFn(data.coc.overall),
          isGroup: true,
          groupKey: 'coc',
          children: Array.isArray(data.coc.campuses) ? data.coc.campuses.map(mapFn) : []
        });
      }
      
      if (data.np && data.np.overall) {
        result.push({
          ...mapFn(data.np.overall),
          isGroup: true,
          groupKey: 'np',
          children: Array.isArray(data.np.campuses) ? data.np.campuses.map(mapFn) : []
        });
      }
      
      return result;
    },
    buildAccordionRows(rows) {
      const result = [];
      let currentGroup = null;
      for (const row of rows) {
        const campusStr = (row.campus || "").toLowerCase().trim();
        if (campusStr === "company-owned campus" || row.type === 'coc') {
          row.isGroup = true;
          row.groupKey = 'coc';
          row.children = [];
          currentGroup = row;
          result.push(row);
        } else if (campusStr === "network campus" || row.type === 'np') {
          row.isGroup = true;
          row.groupKey = 'np';
          row.children = [];
          currentGroup = row;
          result.push(row);
        } else if (campusStr === "overall" || row.type === 'overall' || row.type === 'total') {
          currentGroup = null;
          row.isGroup = false;
          result.push(row);
        } else {
          row.isGroup = false;
          if (currentGroup) {
            currentGroup.children.push(row);
          } else {
            result.push(row);
          }
        }
      }
      return result;
    },
    toggleGroup(table, groupKey) {
      if (!groupKey) return;
      const key = `${table}-${groupKey}`;
      this.$set(this.openGroups, key, !this.openGroups[key]);
    },
    isGroupOpen(table, groupKey) {
      if (!groupKey) return false;
      return !!this.openGroups[`${table}-${groupKey}`];
    },
    dashboardUrl(path, extra = {}) {
      const params = new URLSearchParams();
      params.set("db", this.$store.state.userData.db || "");
      const campusID =
        extra.campusID != null ? extra.campusID : this.apiCampusId;
      params.set("campusID", String(campusID == null || campusID === "" ? "0" : campusID));
      const month = extra.month != null ? extra.month : this.apiMonth;
      params.set("month", String(month || 0));
      const session =
        extra.session != null ? extra.session : this.apiSessionName;
      if (session) params.set("session", session);
      if (extra.key != null && extra.key !== "") {
        params.set("key", String(extra.key));
      }
      if (extra.column != null && extra.column !== "") {
        params.set("column", String(extra.column));
      }
      if (extra.type) params.set("type", extra.type);
      if (extra.examTypeID != null) {
        params.set("examTypeID", extra.examTypeID);
      }
      return `${this.$store.state.domain}IUSSDashboard/${path}?${params.toString()}`;
    },
    async fetchDashboard(path, extra = {}) {
      return this.get({
        url: this.dashboardUrl(path, extra),
        token: this.$store.state.userData.token,
      });
    },
    async loadDashboardData() {
      this.loading = true;
      try {
        const [
          overview,
          admissions,
          retention,
          teacherRetention,
          revenue,
          examGrades,
        ] = await Promise.all([
          this.fetchDashboard("GetEnrollmentOverview"),
          this.fetchDashboard("GetAdmissionsLeadsConversions"),
          this.fetchDashboard("GetStudentRetentionWithdrawal"),
          this.fetchDashboard("GetTeacherRetentionWithdrawal"),
          this.fetchDashboard("GetPredictedRevenueRecovery"),
          this.fetchDashboard("GetExamGradeDistribution", { examTypeID: 1 }),
        ]);

        this.enrollmentOverview =
          overview && typeof overview === "object" && !Array.isArray(overview)
            ? overview
            : null;

        this.admissionsRowsData = this.parseNestedDashboardData(admissions, (row) => ({
          key: row.key,
          campusID: row.campusID,
          campus: row.campus || "—",
          type: row.type,
          leads: row.leads || 0,
          inquiries: row.inquiries || 0,
          conversions: row.conversions || 0,
          currentAdmissions: row.currentAdmissions || 0,
          newSession: row.newSession || 0,
          details: row.details || null,
          _raw: row,
        }));

        this.studentRetentionRowsData = this.parseNestedDashboardData(retention, (row) => ({
          key: row.key,
          campusID: row.campusID,
          campus: row.campus || "—",
          type: row.type,
          total: row.total || 0,
          initiated: row.initiated || 0,
          parentInitiated: row.parentInitiated || 0,
          admissionOfficerCreated: row.admissionOfficerCreated || 0,
          retention: Math.round(
            Number(
              row.retentionPercent != null
                ? row.retentionPercent
                : row.retention
            ) || 0
          ),
          confirmed: row.confirmed || 0,
          retained: row.retained || 0,
          reasons: Array.isArray(row.reasons) ? row.reasons : [],
          details: row.details || null,
          _raw: row,
        }));

        this.teacherRetentionRowsData = this.parseNestedDashboardData(teacherRetention, (row) => ({
          key: row.key,
          campusID: row.campusID,
          campus: row.campus || "—",
          type: row.type,
          initiated: row.initiated || 0,
          retention: Math.round(
            Number(
              row.retentionPercent != null
                ? row.retentionPercent
                : row.retention
            ) || 0
          ),
          confirmed:
            row.confirmed != null
              ? row.confirmed
              : row.left != null
              ? row.left
              : 0,
          left: row.left != null ? row.left : row.confirmed || 0,
          reasons: Array.isArray(row.reasons) ? row.reasons : [],
          details: row.details || null,
          _raw: row,
        }));

        if (revenue && typeof revenue === "object") {
          this.revenueRecoveryDetails = revenue;
          this.chartLabels = Array.isArray(revenue.labels)
            ? revenue.labels
            : [...MONTH_LABELS];
          this.predictedRevenueData = Array.isArray(revenue.predictedRevenue)
            ? revenue.predictedRevenue
            : [];
          this.recoveryData = Array.isArray(revenue.recovery)
            ? revenue.recovery
            : [];
        } else {
          this.revenueRecoveryDetails = null;
          this.chartLabels = [...MONTH_LABELS];
          this.predictedRevenueData = [];
          this.recoveryData = [];
        }

        this.examGradeData = Array.isArray(examGrades) ? examGrades : [];
      } catch (e) {
        console.error("IUSS dashboard load error:", e);
        this.$bvToast.toast("Could not load dashboard data.", {
          title: "Error",
          variant: "danger",
          solid: true,
          toaster: "b-toaster-top-center",
        });
      } finally {
        this.loading = false;
      }
    },
    async loadCampuses() {
      this.campusesLoading = true;
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "campuses?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        });
        const list = Array.isArray(res) ? res : (res && res.data) || [];
        const validCampuses = list.filter(
          (c) => c && c.id != null && Number(c.id) > 0
        );
        this.campusOptions = [
          { id: "coc", branch: "Company-Owned Campus" },
          { id: "np", branch: "Network Campus" },
          ...validCampuses
        ];
        this.filters.campuses = [];
      } catch (e) {
        console.error("Load campuses error:", e);
        this.campusOptions = [];
        this.filters.campuses = [];
      } finally {
        this.campusesLoading = false;
      }
    },
    async loadSessions() {
      this.sessionsLoading = true;
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "Sessions?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        });
        const sessionsData = (res && res.data) || res;
        const list = Array.isArray(sessionsData) ? sessionsData : [];
        this.sessionOptions = [CURRENT_SESSION, ...list];
        this.filters.session = CURRENT_SESSION;
      } catch (e) {
        console.error("Load sessions error:", e);
        this.sessionOptions = [CURRENT_SESSION];
      } finally {
        this.sessionsLoading = false;
      }
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("en-US");
    },
    formatMoney(value) {
      return `Rs ${Number(value || 0).toLocaleString("en-PK")}`;
    },
    formatKey(key) {
      return String(key)
        .replace(/_/g, " ")
        .replace(/([A-Z])/g, " $1")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/^./, (c) => c.toUpperCase());
    },
    formatListHeader(key) {
      const map = {
        name: "Name",
        father_name: "Father name",
        fatherName: "Father name",
        grNo: "GR No",
        grno: "GR No",
        campusName: "Campus",
        campus: "Campus",
        className: "Class",
        class: "Class",
        status: "Status",
        type: "Type",
        gender: "Gender",
        empCode: "Emp code",
        empCodeStr: "Emp code",
        designation: "Designation",
        contact_number: "Contact",
        contactNumber: "Contact",
        leaving_date: "Leaving date",
        leavingDate: "Leaving date",
      };
      return map[key] || this.formatKey(key);
    },
    isCampusColumn(col) {
      const key = String(col || "").toLowerCase();
      return key === "campusname" || key === "campus" || key === "branch";
    },
    isNameColumn(col) {
      const key = String(col || "").toLowerCase();
      return (
        key === "name" ||
        key === "studentname" ||
        key === "staffname" ||
        key === "fullname" ||
        key === "employeename"
      );
    },
    isStatusColumn(col) {
      const key = String(col || "").toLowerCase();
      return key.includes("status") || key === "type";
    },
    statusChipClass(value) {
      const v = String(value || "").toLowerCase();
      if (v.includes("present") || v.includes("active") || v === "company" || v.includes("completed")) {
        return "pill-green";
      }
      if (v.includes("left") || v.includes("withdraw") || v.includes("inactive") || v.includes("reject")) {
        return "pill-orange";
      }
      if (v.includes("pending")) {
        return "pill-blue";
      }
      return "pill-blue";
    },
    formatListCell(value) {
      if (value == null || value === "") return "—";
      if (typeof value === "boolean") return value ? "Yes" : "No";
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          return value.length
            ? value.map((item) => this.formatListCell(item)).join(", ")
            : "—";
        }
        return JSON.stringify(value);
      }
      return this.formatDisplayDate(value);
    },
    formatDisplayDate(value) {
      const raw = String(value).trim();
      if (!raw) return "—";

      // ISO / SQL datetime: 2025-06-28T00:00:00 or 2025-06-28 00:00:00(.fff)(Z)
      const isoMatch = raw.match(
        /^(\d{4}-\d{2}-\d{2})(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/i
      );
      if (isoMatch) return isoMatch[1];

      // 28/06/2025 00:00:00 or 28-06-2025 00:00
      const dmyMatch = raw.match(
        /^(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})(?:\s+\d{1,2}:\d{2}(?::\d{2})?)?$/
      );
      if (dmyMatch) return dmyMatch[1];

      return raw;
    },
    resolveListColumns(list) {
      if (!Array.isArray(list) || !list.length) return [];
      const sample = list[0] || {};
      const skip = new Set([
        "id",
        "sid",
        "sId",
        "sID",
        "SID",
        "db",
        "campusID",
        "campusId",
        "branchID",
        "branchId",
        "studentID",
        "studentId",
        "staffID",
        "staffId",
        "userID",
        "userId",
        "photo",
        "image",
        "avatar",
        "picture",
        "details",
        "_raw",
        "empCode",
        "empCodeStr",
        "employeeCode",
        "code",
        "classCurrent",
        "class_current",
        "sectionCurrent",
        "section_current",
        "classID",
        "classId",
        "class_id",
        "sectionID",
        "sectionId",
        "section_id",
        "tID",
        "tId",
        "teacherID",
        "teacherId",
        "sessionID",
        "sessionId",
        "session_id",
        "source",
        "initiatedBy",
        "initiated_by"
      ]);
      const preferred = [
        "grNo",
        "grno",
        "name",
        "studentName",
        "father_name",
        "fatherName",
        "staffName",
        "fullName",
        "employeeName",
        "class",
        "className",
        "section",
        "sectionName",
        "status",
        "campusName",
        "campus",
        "branch",
        "designation",
        "department",
        "type",
        "reason",
        "leaving_date",
        "leavingDate",
        "session",
        "gender",
        "phone",
        "mobile",
        "contact_number",
        "contactNumber",
        "email",
      ];
      const keys = Object.keys(sample).filter((k) => {
        if (skip.has(k)) return false;
        const val = sample[k];
        return val == null || typeof val !== "object";
      });
      const ordered = preferred.filter((k) => keys.includes(k));
      const rest = keys.filter((k) => !ordered.includes(k));
      return [...ordered, ...rest].slice(0, 8);
    },
    async openEnrollmentList(card) {
      if (!card || !card.key) return;
      this.listModal = {
        show: true,
        title: card.label,
        loading: true,
        key: card.key,
        count: Number(card.value) || 0,
        list: [],
        columns: [],
      };
      try {
        const data = await this.fetchDashboard("GetEnrollmentOverviewList", {
          key: card.key,
        });
        const payload =
          data && typeof data === "object" && !Array.isArray(data)
            ? data
            : { list: Array.isArray(data) ? data : [] };
        const list = Array.isArray(payload.list) ? payload.list : [];
        this.listModal = {
          ...this.listModal,
          loading: false,
          count:
            payload.count != null ? Number(payload.count) || 0 : list.length,
          list,
          columns: this.resolveListColumns(list),
        };
      } catch (e) {
        console.error(e);
        this.listModal = {
          ...this.listModal,
          loading: false,
          list: [],
          columns: [],
        };
        this.$bvToast.toast("Could not load enrollment list.", {
          title: "Error",
          variant: "danger",
          solid: true,
          toaster: "b-toaster-top-center",
        });
      }
    },
    findMonthRow(months, focusMonth) {
      if (!Array.isArray(months) || !focusMonth) return null;
      const target = String(focusMonth).toLowerCase().trim();
      const targetToken = target.split(/[\s\-_]/)[0];
      return (
        months.find((m) => {
          const label = String(m.label || m.month || "").toLowerCase().trim();
          if (!label) return false;
          if (label === target) return true;
          if (label.startsWith(target) || target.startsWith(label)) return true;
          const labelToken = label.split(/[\s\-_]/)[0];
          return labelToken && targetToken && labelToken === targetToken;
        }) || null
      );
    },
    openFinanceModal(metric, focusMonth = null) {
      const isRecovery = metric === "recovery";
      this.financeModal = {
        show: true,
        title: isRecovery ? "Recovery" : "Predicted revenue",
        metric: isRecovery ? "recovery" : "predictedRevenue",
        metricLabel: isRecovery ? "Recovery" : "Predicted revenue",
        focusMonth: focusMonth || null,
      };
    },
    retentionPillClass(value) {
      return Number(value) >= 90 ? "pill-green" : "pill-orange";
    },
    summarizeDetailsRow(row) {
      if (!row) return {};
      const summary = {
        Campus: row.campus,
        Type: row.type,
      };
      if (row.leads != null) summary.Leads = row.leads;
      if (row.inquiries != null) summary.Inquiries = row.inquiries;
      if (row.conversions != null) summary.Conversions = row.conversions;
      if (row.currentAdmissions != null) {
        summary["Current admissions"] = row.currentAdmissions;
      }
      if (row.newSession != null) summary["New session"] = row.newSession;
      if (row.total != null) summary.Total = row.total;
      if (row.initiated != null) summary.Initiated = row.initiated;
      if (row.retention != null) summary["Retention %"] = row.retention;
      if (row.confirmed != null) summary.Confirmed = row.confirmed;
      if (row.retained != null) summary.Retained = row.retained;

      const details = row.details || {};
      if (details.inquiries) {
        summary["Inquiry records"] = details.inquiries.length;
      }
      if (details.conversions) {
        summary["Conversion records"] = details.conversions.length;
      }
      if (details.currentAdmissions) {
        summary["Admission records"] = details.currentAdmissions.length;
      }
      if (details.newSession) {
        summary["New session records"] = details.newSession.length;
      }
      if (details.withdrawals) {
        summary["Withdrawal records"] = details.withdrawals.length;
      }
      if (details.confirmedWithdrawals) {
        summary["Confirmed withdrawals"] = details.confirmedWithdrawals.length;
      }
      if (details.leftStaff) {
        summary["Left staff"] = details.leftStaff.length;
      }
      return summary;
    },
    normalizeReasons(reasons) {
      if (!Array.isArray(reasons) || !reasons.length) return [];
      return reasons
        .map((r) => {
          const people = Array.isArray(r.students)
            ? r.students
            : Array.isArray(r.staff)
            ? r.staff
            : [];
          return {
            label:
              r.reason ||
              r.leavingReason ||
              r.leavingCategory ||
              "Other",
            count: Number(r.count) || people.length || 0,
            people,
            columns: this.resolveListColumns(people),
            open: false,
          };
        })
        .sort((a, b) => b.count - a.count);
    },
    extractListPayload(data) {
      if (!data) return { count: 0, list: [], campus: "" };
      if (Array.isArray(data)) {
        return { count: data.length, list: data, campus: "" };
      }
      if (typeof data !== "object") {
        return { count: 0, list: [], campus: "" };
      }
      const list = Array.isArray(data.list)
        ? data.list
        : Array.isArray(data.leftStaff)
        ? data.leftStaff
        : Array.isArray(data.staff)
        ? data.staff
        : Array.isArray(data.reasons)
        ? data.reasons
        : Array.isArray(data.students)
        ? data.students
        : [];
      return {
        count: data.count != null ? Number(data.count) || 0 : list.length,
        list,
        campus: data.campus || data.campusName || "",
        column: data.column || "",
        key: data.key || "",
        reasons: Array.isArray(data.reasons) ? data.reasons : null,
      };
    },
    async openColumnList({ path, key, column, title, campus }) {
      if (key == null || key === "") return;
      this.listModal = {
        show: true,
        title: campus ? `${title} — ${campus}` : title,
        loading: true,
        key: String(key),
        count: 0,
        list: [],
        columns: [],
      };
      try {
        const data = await this.fetchDashboard(path, { key, column });
        const payload = this.extractListPayload(data);
        this.listModal = {
          ...this.listModal,
          loading: false,
          count: payload.count,
          list: payload.list,
          columns: this.resolveListColumns(payload.list),
          title: payload.campus
            ? `${title} — ${payload.campus}`
            : this.listModal.title,
        };
      } catch (e) {
        console.error(e);
        this.listModal = {
          ...this.listModal,
          loading: false,
          list: [],
          columns: [],
        };
        this.$bvToast.toast("Could not load details list.", {
          title: "Error",
          variant: "danger",
          solid: true,
          toaster: "b-toaster-top-center",
        });
      }
    },
    openAdmissionsColumn(row, column, label) {
      this.openColumnList({
        path: "GetAdmissionsDetails",
        key: row.key,
        column,
        title: label,
        campus: row.campus,
      });
    },
    openStudentRetentionColumn(row, column, label) {
      this.openColumnList({
        path: "GetStudentRetentionWithdrawalDetails",
        key: row.key,
        column,
        title: label,
        campus: row.campus,
      });
    },
    openTeacherRetentionColumn(row, column, label) {
      this.openColumnList({
        path: "GetTeacherRetentionWithdrawalDetails",
        key: row.key,
        column,
        title: label,
        campus: row.campus,
      });
    },
    async openDetails(section, row) {
      this.detailsModal = {
        show: true,
        title: `${section} — ${row.campus}`,
        row: this.summarizeDetailsRow(row),
        reasons: this.normalizeReasons(row.reasons),
      };
    },
    async openReasons(type, row) {
      this.reasonsModal = {
        show: true,
        title: `${type} withdrawal reasons — ${row.campus}`,
        loading: true,
        items: [],
      };

      const detailsPath =
        type === "Teacher"
          ? "GetTeacherRetentionWithdrawalDetails"
          : type === "Student"
          ? "GetStudentRetentionWithdrawalDetails"
          : null;

      if (detailsPath && row && row.key != null && row.key !== "") {
        try {
          const data = await this.fetchDashboard(detailsPath, {
            key: row.key,
            column: "reasons",
          });
          const payload = this.extractListPayload(data);
          const reasons = payload.reasons || payload.list;
          this.reasonsModal = {
            ...this.reasonsModal,
            loading: false,
            items: this.normalizeReasons(reasons),
          };
          return;
        } catch (e) {
          console.error(e);
        }
      }

      this.reasonsModal = {
        ...this.reasonsModal,
        loading: false,
        items: this.normalizeReasons(row.reasons),
      };
    },
    toggleReasonStudents(idx) {
      const items = this.reasonsModal.items.map((item, i) => {
        if (i !== idx) return item;
        if (!item.people || !item.people.length) return item;
        return { ...item, open: !item.open };
      });
      this.reasonsModal = { ...this.reasonsModal, items };
    },
    async downloadExcel() {
      try {
        const rows = await this.fetchDashboard("GetEnrollmentCampusWise");
        const list = Array.isArray(rows) ? rows : [];
        const lines = ["Campus ID,Campus Name,Type,Students,Staff"];
        list.forEach((r) => {
          lines.push(
            [
              r.campusID,
              `"${String(r.campusName || "").replace(/"/g, '""')}"`,
              r.type,
              r.students || 0,
              r.staff || 0,
            ].join(",")
          );
        });

        const blob = new Blob([lines.join("\n")], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `enrollment-campus-wise-${Date.now()}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.$bvToast.toast("Enrollment campus-wise Excel (CSV) downloaded.", {
          title: "Download ready",
          variant: "success",
          solid: true,
          toaster: "b-toaster-top-center",
        });
      } catch (e) {
        console.error(e);
        this.$bvToast.toast("Could not download enrollment Excel.", {
          title: "Error",
          variant: "danger",
          solid: true,
          toaster: "b-toaster-top-center",
        });
      }
    },
  },
};
</script>


<style lang="scss" scoped>
.demo-exec-dashboard {
  --bg: #faf9ff;
  --card: #ffffff;
  --card-hover: #f8f7ff;
  --line: #e0d8ff;
  --text: #2d1b69;
  --muted: #8a86b7;
  --blue: #7c5dfa;
  --purple: #7c5dfa;
  --purple-mid: #9d7aff;
  --purple-light: #cbb9ff;
  --green: #10b981;
  --orange: #f59e0b;
  --pink: #ec4899;
  --teal: #14b8a6;

  min-height: 100vh;
  margin: -1.5rem;
  padding: 1.5rem;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  color: var(--text);
  position: relative;
}

.demo-loading-bar {
  position: sticky;
  top: 0;
  z-index: 5;
  height: 3px;
  margin: -1.5rem -1.5rem 1rem;
  background: linear-gradient(
    90deg,
    transparent,
    #7c5dfa,
    #cbb9ff,
    transparent
  );
  background-size: 200% 100%;
  animation: demoLoadSlide 1.1s linear infinite;
}

@keyframes demoLoadSlide {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

.demo-exec-dashboard.is-loading {
  pointer-events: none;
}

/* ── Professional skeleton ─────────────────────────────────── */
@keyframes skelWave {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@keyframes skelFadeIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.skel-bone {
  display: inline-block;
  vertical-align: middle;
  background: linear-gradient(
    90deg,
    #eeeaf8 0%,
    #f7f5fc 40%,
    #eeeaf8 80%
  );
  background-size: 200% 100%;
  animation: skelWave 1.6s ease-in-out infinite;
  animation-delay: var(--skel-delay, 0s);
  border-radius: 6px;
}

.skel-kpi {
  background: #fff !important;
  border: 1px solid #ebe6f8 !important;
  box-shadow: 0 2px 10px rgba(115, 103, 240, 0.04) !important;
  min-height: 128px;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding: 1rem 1.1rem;
  animation: skelFadeIn 0.35s ease both;
  animation-delay: var(--skel-delay, 0s);

  .skel-kpi-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .skel-kpi-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
  }

  .skel-kpi-badge {
    width: 52px;
    height: 20px;
    border-radius: 999px;
  }

  .skel-kpi-value {
    width: 48%;
    height: 26px;
    border-radius: 8px;
    margin-top: 0.15rem;
  }

  .skel-kpi-label {
    width: 68%;
    height: 12px;
  }

  .skel-kpi-track {
    width: 100%;
    height: 6px;
    border-radius: 999px;
    margin-top: auto;
  }
}

.skel-chart {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
  height: 280px;
  padding: 1.25rem 0.5rem 0.35rem;
  box-sizing: border-box;
}

.skel-chart-bar {
  flex: 1;
  max-width: 36px;
  min-height: 28px;
  border-radius: 8px 8px 4px 4px;
  background: linear-gradient(
    180deg,
    #f4f1fb 0%,
    #ebe6f8 100%
  );
  position: relative;
  overflow: hidden;
  animation: skelFadeIn 0.4s ease both;
  animation-delay: var(--skel-delay, 0s);

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.65) 50%,
      transparent 100%
    );
    background-size: 200% 100%;
    animation: skelWave 1.6s ease-in-out infinite;
    animation-delay: var(--skel-delay, 0s);
  }
}

.skel-row {
  animation: skelFadeIn 0.35s ease both;
  animation-delay: var(--skel-delay, 0s);

  td {
    padding: 0.75rem 0.85rem;
    vertical-align: middle;
  }

  .skel-w-lg {
    width: 110px;
    height: 12px;
  }

  .skel-w-sm {
    width: 42px;
    height: 12px;
  }

  .skel-pill {
    width: 48px;
    height: 22px;
    border-radius: 999px;
  }

  .skel-btn {
    width: 88px;
    height: 28px;
    border-radius: 8px;
  }

  td.text-right .skel-btn + .skel-btn {
    margin-left: 0.45rem;
  }
}

.demo-filter-bar {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.75rem;
  align-items: flex-end;
  margin-bottom: 1.6rem;
  padding: 0.9rem 1rem;
  background: #fff;
  border: 1px solid #e0d8ff;
  border-radius: 16px;
  box-shadow: 0 6px 18px rgba(115, 103, 240, 0.06);
  overflow: visible;
  position: relative;
  z-index: 2;
}

.demo-filter-fields {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 0.9fr) minmax(0, 0.9fr);
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
}

.demo-filter-item {
  min-width: 0;

  label {
    display: block;
    margin-bottom: 0.35rem;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--muted);
  }

  .demo-select {
    width: 100%;
  }
}

.demo-select ::v-deep .vs__dropdown-toggle {
  background: #fff;
  border: 1.5px solid #e0d8ff;
  border-radius: 10px;
  min-height: 44px;
  color: var(--text);
  box-shadow: none;
}

.demo-select-multi ::v-deep .vs__dropdown-toggle {
  min-height: 44px;
  height: auto;
  padding: 0.2rem 0.35rem;
}

.demo-select-multi ::v-deep .vs__selected {
  background: #f3f0ff;
  border: 1px solid #e0d8ff;
  border-radius: 8px;
  color: #7c5dfa !important;
  margin: 0.15rem;
  padding: 0.15rem 0.45rem;
  font-size: 0.78rem;
}

.demo-select ::v-deep .vs__dropdown-toggle:hover {
  border-color: #b8a9ff;
}

.demo-select ::v-deep.vs--open .vs__dropdown-toggle {
  border-color: #7c5dfa;
  box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.15);
}

.demo-select ::v-deep .vs__search,
.demo-select ::v-deep .vs__selected {
  color: var(--text) !important;
  font-weight: 600;
}

.demo-select ::v-deep .vs__dropdown-menu {
  background: #fff !important;
  border: 1px solid #e0d8ff !important;
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(45, 27, 105, 0.12);
  padding: 0.35rem;
  z-index: 5;
}

.demo-select ::v-deep .vs__dropdown-option {
  color: #2d1b69 !important;
  border-radius: 8px;
  padding: 0.55rem 0.75rem;
  margin: 0.1rem 0;
  font-weight: 600;
}

.demo-select ::v-deep .vs__dropdown-option--highlight {
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%) !important;
  color: #ffffff !important;
}

.demo-select ::v-deep .vs__dropdown-option--selected {
  background: #f3f0ff;
  color: #7c5dfa !important;
}

.demo-select ::v-deep .vs__dropdown-option--selected.vs__dropdown-option--highlight {
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%) !important;
  color: #ffffff !important;
}

.demo-excel-btn {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  white-space: nowrap;
  border: 1.5px solid #e0d8ff;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  color: #fff;
  border-radius: 10px;
  padding: 0.7rem 1rem;
  font-weight: 700;
  min-height: 44px;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(124, 93, 250, 0.25);

  &:hover {
    background: linear-gradient(135deg, #6d4cdb 0%, #8b6ff5 100%);
    border-color: #7c5dfa;
    color: #fff;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(124, 93, 250, 0.35);
  }
}

.demo-section {
  margin-bottom: 0.35rem;
}

.demo-section-head {
  margin-bottom: 0.95rem;
}

.demo-section-label {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.72rem;
  color: var(--muted);
  font-weight: 700;
  margin-bottom: 0.2rem;
}

.demo-section-title {
  margin: 0;
  color: var(--text);
  font-size: 1.2rem;
  font-weight: 800;
}

.demo-kpi-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0.95rem;
  margin-bottom: 1.35rem;
}

.demo-kpi-card {
  background: #fff;
  border: 1.5px solid #ebe7ff;
  border-radius: 16px;
  padding: 1.1rem 1.1rem 1rem;
  box-shadow: 0 6px 18px rgba(115, 103, 240, 0.08);
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: #d4c7ff;
    box-shadow: 0 12px 28px rgba(115, 103, 240, 0.16);
    background: #fff;
  }

  &.is-clickable {
    cursor: pointer;

    &:focus {
      outline: none;
      border-color: #7c5dfa;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.18);
    }
  }
}

.demo-kpi-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.7rem;
}

.demo-kpi-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &.icon-blue {
    background: rgba(124, 93, 250, 0.12);
    color: var(--purple);
  }
  &.icon-purple {
    background: rgba(124, 93, 250, 0.12);
    color: var(--purple);
  }
  &.icon-teal {
    background: rgba(20, 184, 166, 0.12);
    color: var(--teal);
  }
  &.icon-green {
    background: rgba(16, 185, 129, 0.12);
    color: var(--green);
  }
  &.icon-orange {
    background: rgba(245, 158, 11, 0.14);
    color: var(--orange);
  }
  &.icon-pink {
    background: rgba(236, 72, 153, 0.12);
    color: var(--pink);
  }
}

.demo-kpi-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
  border-radius: 999px;
  padding: 0.2rem 0.55rem;
  font-size: 0.7rem;
  font-weight: 700;
}

.demo-kpi-value {
  font-size: 1.7rem;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: var(--purple);
}

.demo-kpi-label {
  margin-top: 0.35rem;
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--text);
}

.demo-kpi-sub {
  margin-top: 0.2rem;
  color: var(--muted);
  font-size: 0.78rem;
}

.demo-kpi-track {
  margin-top: 0.7rem;
  height: 5px;
  border-radius: 999px;
  background: #f0edff;
  overflow: hidden;
}

.demo-kpi-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
}

.demo-charts-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.15rem;
}

.demo-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 16px;
  padding: 1.1rem 1.15rem 0.85rem;
  margin-bottom: 1.15rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.08);
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  &.is-clickable {
    cursor: pointer;

    &:hover {
      transform: translateY(-2px);
      border-color: #d4c7ff;
      box-shadow: 0 12px 28px rgba(115, 103, 240, 0.14);
    }

    &:focus {
      outline: none;
      border-color: #7c5dfa;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.18);
    }
  }
}

.demo-chart-card {
  margin-bottom: 0;
}

.demo-card-head {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.35rem;

  h5 {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: var(--text);
  }
}

.demo-card-icon {
  width: 30px;
  height: 30px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &.orange {
    background: rgba(245, 158, 11, 0.14);
    color: var(--orange);
  }
  &.green {
    background: rgba(20, 184, 166, 0.12);
    color: var(--teal);
  }
  &.purple {
    background: rgba(124, 93, 250, 0.12);
    color: var(--purple);
  }
}

.demo-table-wrap {
  overflow-x: auto;
}

.demo-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 760px;

  th,
  td {
    padding: 0.85rem 0.65rem;
    border-bottom: 1px solid #f0edff;
    vertical-align: middle;
    font-size: 0.9rem;
    color: var(--text);
  }

  th {
    color: var(--muted);
    font-size: 0.74rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 700;
  }

  tbody tr {
    transition: background 0.15s ease;

    &:hover {
      background: #f8f7ff;
    }

    &:last-child td {
      border-bottom: none;
    }
  }
}

.campus-cell {
  font-weight: 700;
}

.pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 42px;
  padding: 0.22rem 0.55rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 800;
}

.pill-blue {
  background: rgba(124, 93, 250, 0.12);
  color: #7c5dfa;
}

.pill-green {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}

.pill-orange {
  background: rgba(245, 158, 11, 0.14);
  color: #d97706;
}

.demo-action-btn {
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--line);
  background: #fff;
  color: var(--text);
  border-radius: 8px;
  padding: 0.35rem 0.7rem;
  font-size: 0.78rem;
  font-weight: 700;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(124, 93, 250, 0.1);
    border-color: #7c5dfa;
    color: #7c5dfa;
  }
}

.demo-metric-btn {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  border: none;
  background: transparent;
  color: var(--text);
  font-weight: 700;
  padding: 0.15rem 0.25rem;
  border-radius: 6px;
  transition: all 0.15s ease;

  &:hover {
    color: #7c5dfa;
    background: rgba(124, 93, 250, 0.08);
    text-decoration: underline;
  }
}

.actions-cell {
  display: flex;
  justify-content: flex-end;
  gap: 0.45rem;
}

.demo-modal-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.demo-modal-item {
  background: #f8f7ff;
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  padding: 0.7rem 0.8rem;
}

.demo-modal-key {
  display: block;
  color: var(--muted);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.25rem;
}

.demo-modal-val {
  font-weight: 700;
  font-size: 1rem;
  color: var(--text);
}

.demo-modal-note {
  margin-top: 1rem;
  margin-bottom: 0;
  color: var(--muted);
  font-size: 0.82rem;
}

.demo-modal-reasons {
  margin-top: 1rem;
  padding: 0.85rem 0.9rem;
  background: #f8f7ff;
  border: 1px solid #e0d8ff;
  border-radius: 10px;

  > .demo-modal-key {
    margin-bottom: 0.55rem;
  }
}

.demo-reason-list {
  display: flex;
  flex-direction: column;
}

.demo-reason-list-scroll {
  flex: 1 1 auto;
  min-height: 0;
  max-height: min(58vh, 520px);
  overflow: auto;
}

.demo-reason-block {
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
}

.demo-reason-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding: 0.8rem 0.35rem;
  border: none;
  background: transparent;
  text-align: left;

  &.is-expandable {
    cursor: pointer;
    border-radius: 8px;

    &:hover {
      background: #f8fafc;
    }
  }
}

.demo-reason-label {
  color: #1e293b;
  font-weight: 700;
  font-size: 0.95rem;
  line-height: 1.35;
  flex: 1;
  min-width: 0;
  display: inline-flex;
  align-items: center;
}

.demo-reason-count {
  flex-shrink: 0;
  min-width: 36px;
  text-align: center;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  background: #f1f5f9;
  color: #7c5dfa;
  font-size: 0.8rem;
  font-weight: 800;
}

.demo-reason-students {
  padding: 0 0.25rem 0.75rem;
}

.demo-reason-people-wrap {
  max-height: 240px;
  border: 1px solid #eef2f7;
  border-radius: 8px;
}

.demo-reason-empty {
  color: #64748b;
  font-size: 0.9rem;
  padding: 1.5rem 0.5rem;
  text-align: center;
}

.demo-list-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.85rem;
}

.demo-finance-blocks {
  flex: 1 1 auto;
  min-height: 0;
  max-height: min(62vh, 580px);
  overflow: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.25rem;
  padding: 0.5rem 0.5rem 1rem;
  -webkit-overflow-scrolling: touch;
}

.demo-campus-card {
  border: 1px solid #eef2f7;
  border-radius: 14px;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  }
}

.demo-campus-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.15rem;
  background: linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
}

.demo-campus-card-info {
  min-width: 0;
}

.demo-campus-card-name {
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.3;
  word-break: break-word;
}

.demo-campus-card-meta {
  margin-top: 0.25rem;
  font-size: 0.76rem;
  color: #64748b;
  text-transform: capitalize;
  font-weight: 500;
}

.demo-campus-card-total {
  flex-shrink: 0;
  text-align: right;

  span {
    display: block;
    font-size: 0.7rem;
    color: #64748b;
    margin-bottom: 0.15rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
  }

  strong {
    display: block;
    font-size: 1.05rem;
    color: #7c5dfa;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
}

.demo-campus-card-body {
  padding: 0.5rem 0.5rem;
}

.demo-campus-fee-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f8fafc;
  }

  span {
    min-width: 0;
    font-size: 0.88rem;
    color: #475569;
    font-weight: 500;
    word-break: break-word;
  }

  strong {
    flex-shrink: 0;
    font-size: 0.9rem;
    color: #0f172a;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
}

.demo-list-modal-content {
  border-radius: 16px !important;
  border: none !important;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
  overflow: hidden;
}

.demo-list-table-wrap {
  flex: 1 1 auto;
  min-height: 0;
  max-height: min(60vh, 560px);
  overflow: auto;
  background: #fff;
  -webkit-overflow-scrolling: touch;
}

.demo-list-shell {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.demo-list-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  margin-bottom: 0;
  border-bottom: 1px solid #eef2f7;
  background: linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%);
}

.demo-list-header-text {
  min-width: 0;
}

.demo-list-title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
  color: #1e293b;
  line-height: 1.3;
}

.demo-list-sub {
  margin: 0.3rem 0 0;
  font-size: 0.8rem;
  color: #6b7280;
  font-weight: 500;
  line-height: 1.4;

  span {
    margin: 0 0.28rem;
    color: #d1d5db;
  }
}

.demo-list-close {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  color: #6b7280;
  padding: 0;
  transition: all 0.15s ease;

  &:hover {
    background: #f9fafb;
    color: #111827;
    border-color: #d1d5db;
  }
}

.demo-list-table {
  width: 100%;
  min-width: 860px;
  border-collapse: separate;
  border-spacing: 0;

  thead th {
    position: sticky;
    top: 0;
    z-index: 3;
    background: #f8fafc;
    color: #64748b;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
    padding: 0.85rem 1rem;
  }

  td {
    padding: 0.85rem 1rem;
    white-space: nowrap;
    font-size: 0.875rem;
    color: #334155;
    background: #fff;
    border-bottom: 1px solid #f1f5f9;
    transition: background-color 0.15s ease;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover td {
    background: #f8fafc;
  }

  .col-index {
    width: 48px;
    color: #9ca3af;
    font-weight: 500;
    text-align: left;
  }

  .col-name {
    min-width: 150px;
    font-weight: 600;
    color: #111827;
  }

  .col-campus {
    min-width: 140px;
    font-weight: 600;
    color: #111827;
  }

  .col-status {
    text-transform: capitalize;
    color: #6b7280;
  }
}

.demo-list-loading {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding: 0.35rem 0 0.75rem;
}

.demo-list-skel-row {
  display: grid;
  grid-template-columns: 1.4fr 0.7fr 0.7fr 0.9fr;
  gap: 0.75rem;
  align-items: center;
  padding: 0.55rem 0;
  border-bottom: 1px solid #f0edff;

  .skel-w-lg,
  .skel-w-sm,
  .skel-btn {
    height: 12px;
  }

  .skel-w-lg {
    width: 100%;
  }

  .skel-w-sm {
    width: 70%;
  }

  .skel-btn {
    width: 55%;
    height: 22px;
    border-radius: 8px;
    justify-self: end;
  }
}

@media (max-width: 1200px) {
  .demo-kpi-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .demo-finance-blocks {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .demo-exec-dashboard {
    padding: 1rem 0.75rem 1.5rem;
  }

  .demo-charts-row {
    grid-template-columns: 1fr;
  }

  .demo-filter-bar {
    flex-wrap: wrap;
    padding: 0.85rem;
  }

  .demo-filter-fields {
    grid-template-columns: 1fr;
    width: 100%;
  }

  .demo-excel-btn {
    width: 100%;
    justify-content: center;
  }

  .demo-kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .demo-finance-blocks {
    grid-template-columns: 1fr;
    max-height: min(58vh, 520px);
  }

  .demo-list-table {
    min-width: 640px;
  }

  .demo-table {
    min-width: 640px;
  }

  .demo-campus-card-head {
    flex-direction: column;
    align-items: stretch;
  }

  .demo-campus-card-total {
    text-align: left;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    padding-top: 0.35rem;
    border-top: 1px dashed #e2e8f0;
    margin-top: 0.35rem;

    span {
      margin-bottom: 0;
    }
  }
}

.accordion-child-row {
  background-color: #faf9ff; /* slightly tinted to distinguish children */
  animation: fadeInAccordion 0.4s ease-out forwards;
  
  /* Make text slightly smaller or indent properly */
  td {
    font-size: 0.95em;
  }
}

@keyframes fadeInAccordion {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.demo-table tbody tr.is-clickable.group-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #f3f0ff;
  }
}

@media (max-width: 560px) {
  .demo-exec-dashboard {
    padding: 0.75rem 0.5rem 1.25rem;
  }

  .demo-kpi-grid {
    grid-template-columns: 1fr;
  }

  .demo-modal-grid {
    grid-template-columns: 1fr;
  }

  .demo-section-title {
    font-size: 1.05rem;
  }

  .demo-list-title {
    font-size: 0.98rem;
  }

  .demo-list-sub {
    font-size: 0.74rem;
  }

  .demo-list-table {
    min-width: 520px;
  }

  .demo-table {
    min-width: 520px;
  }

  .demo-action-btn {
    padding: 0.3rem 0.55rem;
    font-size: 0.72rem;
  }

  .actions-cell {
    flex-wrap: wrap;
    justify-content: flex-start;
  }
}
</style>

<style lang="scss">
/* vue-select dropdown contrast (menu can render outside scoped root) */
.demo-select .vs__dropdown-option {
  color: #2d1b69 !important;
  font-weight: 600;
}

.demo-select .vs__dropdown-option--highlight,
.demo-select .vs__dropdown-option--highlight.vs__dropdown-option--selected {
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%) !important;
  color: #ffffff !important;
}

.demo-select .vs__dropdown-option--selected {
  background: #f3f0ff;
  color: #7c5dfa !important;
}

.demo-dark-modal .modal-content.demo-dark-modal-content {
  background: #ffffff;
  color: #2d1b69;
  border: 1px solid #e0d8ff;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.15);
  overflow: hidden;
}

.demo-dark-modal .demo-dark-modal-header {
  border-bottom: 1px solid #f0edff;
  background: #fff;
}

.demo-dark-modal .demo-dark-modal-body {
  background: #fff;
  max-height: 72vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.demo-dark-modal .demo-dark-modal-body .demo-list-table-wrap,
.demo-dark-modal .demo-dark-modal-body .demo-list-loading {
  flex: 1 1 auto;
  min-height: 0;
}

.demo-dark-modal .demo-dark-modal-title {
  color: #2d1b69;
  font-weight: 700;
}

.demo-dark-modal .close {
  color: #7c5dfa;
  text-shadow: none;
  opacity: 0.85;
}

.demo-list-modal .modal-content.demo-list-modal-content {
  background: #fff;
  color: #111827;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
  overflow: hidden;
}

.demo-list-modal .demo-list-modal-body {
  background: #fff;
  padding: 1rem 1.15rem 1.1rem;
  max-height: 78vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

@media (max-width: 900px) {
  .demo-list-modal .modal-dialog {
    margin: 0.5rem;
    max-width: calc(100% - 1rem);
  }

  .demo-list-modal .demo-list-modal-body {
    padding: 0.85rem 0.85rem 0.95rem;
    max-height: 85vh;
  }

  .demo-dark-modal .modal-dialog {
    margin: 0.5rem;
    max-width: calc(100% - 1rem);
  }
}

@media (max-width: 560px) {
  .demo-list-modal .modal-dialog,
  .demo-dark-modal .modal-dialog {
    margin: 0.35rem;
    max-width: calc(100% - 0.7rem);
  }

  .demo-list-modal .demo-list-modal-body {
    padding: 0.75rem;
    max-height: 88vh;
  }
}
</style>
